import * as React from 'react';

import { useOffers } from './useOffers';
import {
  OfferDesktop,
  OfferDesktopSkeleton,
  OffersListMobile,
  OffersListDesktop,
  OfferMobile,
  OfferMobileSkeleton,
} from './components';
import { EStatus } from 'shared/types/status';
import { useDeviceType } from '@cian/ui-kit';
import { useIsEditor } from 'shared/utils/editorProvider/editorProvider';

const fill = (n: number, cb: (index: number) => React.ReactNode) => new Array(n).fill(0).map((_, index) => cb(index));

export interface IOffersListProps {
  url: string;
  oneLine?: boolean;
  staticRegion?: boolean;
  /**
   * Управляет загрузкой офферов.
   * Прокидывается из обёртки OffersListLazyWrapper.
   */
  shouldLoad?: boolean;
  /**
   * Управляет отображением кнопки "Посмотреть ещё".
   */
  hideShowMoreButton?: boolean;
}

const DESKTOP_OFFERS_LIMIT = 8;
const DESKTOP_ONE_LINE_OFFERS_LIMIT = 4;
const MOBILE_OFFERS_LIMIT = 6;

export const OffersList: React.FC<IOffersListProps> = ({
  url,
  oneLine = false,
  staticRegion = false,
  shouldLoad,
  hideShowMoreButton = false,
}) => {
  const device = useDeviceType();
  const isEditor = useIsEditor();

  const isMobile = device === 'phone';
  const limit = isMobile ? MOBILE_OFFERS_LIMIT : oneLine ? DESKTOP_ONE_LINE_OFFERS_LIMIT : DESKTOP_OFFERS_LIMIT;

  const { offers, loadStatus, onOfferClick, onShow, onMoreClick, urlToUse } = useOffers({
    url,
    limit,
    isEditor,
    staticRegion,
    shouldLoad,
  });

  const skeleton = React.useMemo(
    () =>
      isMobile ? (
        <OffersListMobile hideShowMoreButton={hideShowMoreButton}>
          {fill(MOBILE_OFFERS_LIMIT, index => (
            <OfferMobileSkeleton key={`offer-mobile-skeleton-${index}`} />
          ))}
        </OffersListMobile>
      ) : (
        <OffersListDesktop hideShowMoreButton={hideShowMoreButton}>
          {fill(oneLine ? DESKTOP_ONE_LINE_OFFERS_LIMIT : DESKTOP_OFFERS_LIMIT, index => (
            <OfferDesktopSkeleton key={`offer-desktop-skeleton-${index}`} />
          ))}
        </OffersListDesktop>
      ),
    [isMobile, oneLine, hideShowMoreButton],
  );

  if (loadStatus === EStatus.Loading) {
    return skeleton;
  }

  if (!offers.length) {
    return null;
  }

  return isMobile ? (
    <OffersListMobile
      url={offers.length === limit ? urlToUse : undefined}
      onShow={onShow}
      onMoreClick={onMoreClick}
      hideShowMoreButton={hideShowMoreButton}
    >
      {offers.map((offer, index) => (
        <OfferMobile position={index + 1} key={`offer-${index}`} onClick={onOfferClick} {...offer} />
      ))}
    </OffersListMobile>
  ) : (
    <OffersListDesktop
      url={offers.length === limit ? urlToUse : undefined}
      onShow={onShow}
      onMoreClick={onMoreClick}
      hideShowMoreButton={hideShowMoreButton}
    >
      {offers.map((offer, index) => (
        <OfferDesktop position={index + 1} key={`offer-${index}`} onClick={onOfferClick} {...offer} />
      ))}
    </OffersListDesktop>
  );
};
